import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, AlertCircle, ChevronRight, Loader2, Shield } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { supabase } from '../lib/supabaseClient';
import backgroundImage from '../assets/trading-background.jpg';

const SignInPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState<'login' | '2fa'>('login');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [userId, setUserId] = useState<string | null>(null);
  const [autoSignIn, setAutoSignIn] = useState(false);
  const navigate = useNavigate();

  const { signIn, check2FAStatus } = useAuth();

  const performSignIn = useCallback(async (emailVal: string, passwordVal: string) => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-credentials`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ email: emailVal, password: passwordVal }),
        }
      );

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Invalid credentials');
      }

      if (data.has2FA) {
        setUserId(data.userId);
        setStep('2fa');
        setAutoSignIn(false);
        setLoading(false);
        return;
      }

      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: emailVal,
        password: passwordVal,
      });

      if (signInError) {
        throw signInError;
      }

      sessionStorage.removeItem('marketLoadingShown');
      navigate('/');
    } catch (err: any) {
      setError(err.message);
      setAutoSignIn(false);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const paramEmail = searchParams.get('email');
    const paramPassword = searchParams.get('password');

    if (paramEmail && paramPassword) {
      setEmail(paramEmail);
      setPassword(paramPassword);
      window.history.replaceState({}, '', '/signin');
      setAutoSignIn(true);
      performSignIn(paramEmail, paramPassword);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await performSignIn(email, password);
  };

  const handleVerify2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (!userId) {
        throw new Error('User ID not found');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-otp`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            userId,
            token: twoFactorCode,
          }),
        }
      );

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Invalid verification code');
      }

      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        throw signInError;
      }

      sessionStorage.removeItem('marketLoadingShown');
      navigate('/', { replace: true });
    } catch (error: any) {
      setError(error.message);
      if (error.message.includes('Invalid')) {
        setStep('login');
        setEmail('');
        setPassword('');
        setTwoFactorCode('');
      }
    } finally {
      setLoading(false);
    }
  };

  if (autoSignIn && loading) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center">
        <div className="w-8 h-8 border-2 border-black/10 border-t-red-700 rounded-full animate-spin mb-4" />
        <p className="text-black/60 text-sm">Signing you in...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Company Name Header */}
      <div className="bg-white w-full border-b border-black/10">
        <div className="max-w-7xl mx-auto px-8 py-4">
          <img src="/logo.svg" alt="Logo" className="h-8" />
        </div>
      </div>

      {/* Full-width Red Header Bar */}
      <div className="bg-red-700 w-full">
        <div className="max-w-7xl mx-auto px-8 py-6 flex items-center justify-between">
          <h1 className="text-white text-xl font-medium">
            Sign in to access your trading dashboard
          </h1>
          <div className="[&_button]:bg-transparent [&_button]:border-white/20 [&_button]:hover:border-white/40 [&_button]:text-white [&_svg]:text-white/60 [&_.absolute]:bg-white [&_.absolute]:border-black/15 [&_.absolute]:rounded-none [&_.absolute_button]:hover:bg-black/5 [&_.absolute_button]:text-black [&_.absolute_button.bg-slate-700\\/50]:bg-transparent [&_.absolute_button.bg-slate-700\\/50]:text-red-700 [&_.absolute_button.bg-slate-700\\/50]:border-l-2 [&_.absolute_button.bg-slate-700\\/50]:border-l-red-700 [&_.absolute_svg.text-blue-400]:text-red-700">
            <div className="flex items-center gap-4">
              <Link to="/about" className="text-sm text-white hover:underline whitespace-nowrap">About Valore</Link>
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      </div>

      {/* Three-Column Layout */}
      <div className="bg-white max-w-7xl mx-auto px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* LEFT COLUMN - Login Form */}
          <div className="max-w-md">
            {error && (
              <div className="mb-6 text-sm text-red-700 border-l-2 border-red-700 pl-4 py-2">
                {error}
              </div>
            )}

            {step === 'login' ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-xs uppercase tracking-wide text-black/70 mb-2 font-medium">{t('auth.email')}</label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-black/30" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('auth.enterYourEmail')}
                      className="w-full bg-white text-black pl-10 pr-4 py-3 border border-black/15 rounded focus:outline-none focus:border-red-700 transition-colors placeholder:text-black/30"
                      required
                    />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-xs uppercase tracking-wide text-black/70 font-medium">{t('auth.password')}</label>
                    <Link to="/forgot-password" className="text-xs text-black/70 hover:text-red-700 transition-colors">
                      {t('auth.forgotPassword')}
                    </Link>
                  </div>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-black/30" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t('auth.enterYourPassword')}
                      className="w-full bg-white text-black pl-10 pr-10 py-3 border border-black/15 rounded focus:outline-none focus:border-red-700 transition-colors placeholder:text-black/30"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-black/30 hover:text-black transition-colors"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#d91222] text-white hover:text-red-900 disabled:text-black/20 py-4 font-semibold transition-colors flex items-center justify-center gap-2 mt-2 border border-red-700 hover:border-red-900 disabled:border-black/10 rounded"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-black/10 border-t-red-700 animate-spin" />
                  ) : (
                    <>
                      {t('auth.signIn')}
                      <ChevronRight size={16} />
                    </>
                  )}
                </button>

                <div className="mt-8 pt-6 border-t border-black/10">
                  <p className="text-black/70 text-sm">
                    {t('auth.dontHaveAccount')}{' '}
                    <Link to="/sign-up" className="text-black hover:text-red-700 transition-colors font-medium underline">
                      {t('auth.createAccount')}
                    </Link>
                  </p>
                </div>

                <div className="mt-4 text-xs text-black/50">
                  {t('auth.secureConnection')}
                </div>
              </form>
            ) : (
              <form onSubmit={handleVerify2FA} className="space-y-6">
                <div className="mb-6">
                  <div className="flex items-center gap-3 mb-4 pb-4 border-b border-black/10">
                    <Shield size={20} className="text-black" />
                    <h3 className="text-xl font-semibold text-black">Two-Factor Authentication</h3>
                  </div>
                  <p className="text-black/70 text-sm">Enter the 6-digit code from your authenticator app</p>
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-wide text-black/50 mb-2 font-medium">Verification Code</label>
                  <input
                    type="text"
                    value={twoFactorCode}
                    onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').substring(0, 6))}
                    placeholder="Enter 6-digit code"
                    className="w-full bg-white text-black px-4 py-3 border border-black/15 rounded focus:outline-none focus:border-red-700 transition-colors text-center text-2xl tracking-widest font-mono"
                    maxLength={6}
                    required
                    autoFocus
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setStep('login');
                      setTwoFactorCode('');
                      setError('');
                    }}
                    className="flex-1 bg-transparent text-black/60 hover:text-black py-4 font-semibold transition-colors border border-black/15 hover:border-black/25 rounded"
                    disabled={loading}
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={loading || twoFactorCode.length !== 6}
                    className="flex-1 bg-transparent text-red-700 hover:text-red-900 disabled:text-black/20 py-4 font-semibold transition-colors flex items-center justify-center gap-2 border border-red-700 hover:border-red-900 disabled:border-black/10 rounded"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-black/10 border-t-red-700 animate-spin" />
                    ) : (
                      'Verify'
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* CENTER COLUMN - Bitcoin Visual */}
          <div className="flex flex-col items-center justify-center text-center space-y-6">
            <img
              src="https://sunny-gaufre-664069.netlify.app/capital/en/1.png"
              alt="Bitcoin Guide"
              className="w-full rounded-lg"
            />
            <p className="text-lg text-black/70 font-medium">
              Secure, convenient trading anytime
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignInPage;
